import { useCallback, useRef } from 'react';

/**
 * Splices text at the current caret of a controlled text input/textarea, then restores the caret just
 * past the inserted text. The consumer attaches `ref` to the field's `inputRef` and calls
 * `insert(text, value, onChange)` from an insert-token menu; passing the field's current controlled
 * `value` + `onChange` at call time means this composes with both RHF-controlled and local-state fields
 * without reaching into the DOM value (which would desync a controlled component). When the field can't
 * be reached (never focused / no selection api) the text is appended to the end as a safe fallback.
 *
 * Caret restoration runs on the next frame — after the controlled re-render has painted the new value —
 * and is best-effort (`setSelectionRange` throws on some input types), so a failure never blocks the edit.
 */
export function useInsertAtCursor<
  T extends HTMLInputElement | HTMLTextAreaElement = HTMLTextAreaElement,
>() {
  const ref = useRef<T | null>(null);

  const insert = useCallback(
    (text: string, value: string, onChange: (next: string) => void) => {
      const el = ref.current;
      const hasSelection =
        !!el && typeof el.selectionStart === 'number' && typeof el.selectionEnd === 'number';
      const start = hasSelection ? (el as T).selectionStart! : value.length;
      const end = hasSelection ? (el as T).selectionEnd! : value.length;
      const next = value.slice(0, start) + text + value.slice(end);
      onChange(next);

      const caret = start + text.length;
      requestAnimationFrame(() => {
        const node = ref.current;
        if (!node) return;
        node.focus();
        try {
          node.setSelectionRange(caret, caret);
        } catch {
          /* setSelectionRange throws on number/email inputs; caret restore is best-effort. */
        }
      });
    },
    [],
  );

  return { ref, insert };
}
